import { Allergen, CerealSubAllergen, Dietary, NutSubAllergen } from '@/generated/gql/graphql'
import { numberFormat } from '@/helpers/numbers'
import { DietaryRow } from '@fusion/dietary/row'
import { AddShoppingCart } from '@mui/icons-material'
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'

import React, { useCallback, useMemo } from 'react'

import { optionsAreValid } from '../ccMenuSpecificHelpers'
import ItemCard from './shared/ItemCard'

type CCMenuItemCardItemData = {
  submenuId: string
  quantityRemaining: number | null
  liveVariation: {
    priceCustomerFacing: number | null
    varType: string | null
  } | null
  liveMenuItem: {
    _id: string
    name: string | null
    image: string | null
    dietaries: Dietary[]
    allergens: Allergen[]
    isCombo: boolean | null
    shortDescription: string | null
    subAllergens: {
      nuts: NutSubAllergen[]
      cereals: CerealSubAllergen[]
    }
    options:
      | ({
          _id: string
          minQty: number | null
          maxQty: number | null
          qtyPerPerson: number | null
        } | null)[]
      | null
  }
}

type Props = {
  itemData: CCMenuItemCardItemData
  vendorLogo: string | null | undefined
  vendorCompanyName: string
  hidePrices: boolean
  showHighlighted?: boolean
  onSeeMoreClick: (itemId: string) => void
  onAddToCart: (itemId: string) => void
}

const CCMenuItemCard: React.FC<Props> = ({
  hidePrices,
  itemData,
  vendorLogo,
  vendorCompanyName,
  showHighlighted,
  onSeeMoreClick,
  onAddToCart,
}) => {
  const { liveMenuItem, quantityRemaining, liveVariation: ccVariation } = itemData

  const handleSeeMoreClick = useCallback(() => {
    onSeeMoreClick(liveMenuItem._id)
  }, [onSeeMoreClick, liveMenuItem._id])

  const handleAddToCart = useCallback(() => {
    if (liveMenuItem.options && optionsAreValid(liveMenuItem.options, {})) {
      onAddToCart(liveMenuItem._id)
      // }
    } else {
      onSeeMoreClick(liveMenuItem._id)
    }
  }, [onAddToCart, liveMenuItem, handleSeeMoreClick])

  const formattedPrice = useMemo(() => {
    return numberFormat((ccVariation?.priceCustomerFacing || 0) / 100)
  }, [ccVariation?.priceCustomerFacing])

  return (
    <ItemCard
      showHighlighted={showHighlighted}
      vendorAvatar={vendorLogo}
      vendorName={vendorCompanyName}
      imageUrl={liveMenuItem.image}
      imageTitle={liveMenuItem.name}
      onClick={handleSeeMoreClick}>
      <CardHeader title={liveMenuItem.name} />
      <CardContent>
        <Stack direction="column" spacing={1}>
          <Typography variant="body2">{liveMenuItem.shortDescription}</Typography>
          <Typography color="info.main" variant="body2">
            {typeof quantityRemaining === 'number' &&
            quantityRemaining > 0 &&
            quantityRemaining < 10
              ? `Only ${quantityRemaining} left`
              : null}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={0.5} direction="row">
              <DietaryRow
                isComboItem={Boolean(liveMenuItem.isCombo)}
                maxToShow={3}
                subAllergens={liveMenuItem.subAllergens}
                allergens={liveMenuItem.allergens}
                dietaries={liveMenuItem.dietaries}
              />
            </Stack>
            {!hidePrices ? <Typography variant="body1">{formattedPrice}</Typography> : null}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions onClick={(e) => e.stopPropagation()}>
        <Stack width="100%" direction="row-reverse" alignItems="center" spacing={0.5}>
          {typeof quantityRemaining === 'number' && quantityRemaining <= 0 ? (
            <Button variant="contained" size="small" disabled>
              Sold out
            </Button>
          ) : (
            <IconButton onClick={handleAddToCart}>
              <AddShoppingCart color="primary" />
            </IconButton>
          )}
          <Button onClick={handleSeeMoreClick} size="small" variant="contained" color="primary">
            See more
          </Button>
        </Stack>
      </CardActions>
    </ItemCard>
  )
}

export default React.memo(CCMenuItemCard, (prev, next) => {
  return (
    prev.hidePrices === next.hidePrices &&
    prev.itemData.quantityRemaining === next.itemData.quantityRemaining &&
    prev.itemData.liveMenuItem === next.itemData.liveMenuItem &&
    prev.vendorLogo === next.vendorLogo &&
    prev.vendorCompanyName === next.vendorCompanyName &&
    prev.showHighlighted === next.showHighlighted
  )
})
